// src/components/nanny/NannyDetailModal.jsx
import { X, Shield, Phone, Clock, Briefcase, Award, CheckCircle } from 'lucide-react';
import Button from '../layout/ui/Button';
import { useState } from 'react';
import MakeOfferModal from './MakeOfferModal';
import { success } from '../common/Toast';
import RatingDisplay from '../common/RatingDisplay';

export default function NannyDetailModal({ nanny, isOpen, onClose }) {
  const [offerOpen, setOfferOpen] = useState(false);

  if (!isOpen || !nanny) return null;

  const formatSchedule = () => {
    if (!nanny.working_schedule) return 'Flexible Schedule';
    const s = nanny.working_schedule;
    const type = (s.schedule_type || 'full_time')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
    
    if (!s.preferred_days?.length) return type;
    return `${type} • ${s.preferred_days.join(', ')}`;
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-8 lg:inset-auto lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 z-50">
        <div className="bg-white rounded-3xl shadow-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white/95 backdrop-blur border-b border-gray-100 px-8 py-6 flex justify-between items-center z-10">
            <h2 className="text-3xl font-black text-gray-900">Nanny Profile</h2>
            <button
              onClick={onClose}
              className="p-3 hover:bg-gray-100 rounded-xl transition-all"
            >
              <X size={28} className="text-gray-600" />
            </button>
          </div>

          <div className="p-8 lg:p-12">
            {/* Hero Section */}
            <div className="text-center mb-12">
              {/* Profile Picture */}
              <div className="relative inline-block mb-8">
                <div className="w-48 h-48 rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
                  {nanny.profile_picture && !nanny.profile_picture.includes('default-avatar') ? (
                    <img
                      src={process.env.REACT_APP_IMAGE_URL + nanny.profile_picture}
                      alt={nanny.full_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-8xl font-black">
                      {nanny.full_name?.[0] || 'N'}
                    </div>
                  )}
                </div>

                {/* Vetting Badge */}
                {nanny.is_vetted && (
                  <div className="absolute -top-4 -right-4 bg-green-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 font-bold">
                    <Shield size={20} />
                    <span className="text-sm">Vetted</span>
                  </div>
                )}
              </div>

              {/* Name & Rating */}
              <h1 className="text-5xl font-black text-gray-900 mb-3">{nanny.full_name}</h1>
              <div className="flex justify-center mb-4">
                <RatingDisplay rating={nanny.rating || 0} size="lg" />
              </div>

              {/* Phone */}
              <p className="text-xl text-gray-700 flex items-center justify-center gap-3">
                <Phone size={24} className="text-primary" />
                {nanny.phone_number}
              </p>
            </div>

            {/* Working Schedule Card */}
            {nanny.working_schedule && (
              <div className="mb-10 p-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-3xl border-2 border-emerald-200">
                <div className="flex items-center gap-4 mb-4">
                  <Clock size={36} className="text-emerald-700" />
                  <h3 className="text-3xl font-bold text-emerald-900">Availability</h3>
                </div>
                <p className="text-2xl font-bold text-emerald-800">
                  {formatSchedule()}
                </p>
                {nanny.working_schedule.notes && (
                  <p className="mt-5 text-lg text-emerald-700 italic bg-white/70 rounded-2xl px-6 py-4">
                    “{nanny.working_schedule.notes}”
                  </p>
                )}
              </div>
            )}

            {/* Experience & Qualifications */}
            <div className="space-y-10">
              {/* Experience */}
              <div>
                <h3 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <Briefcase size={32} />
                  Work Experience
                  {nanny.years_of_experience > 0 && (
                    <span className="ml-3 text-xl font-normal text-gray-600">
                      ({nanny.years_of_experience} {nanny.years_of_experience === 1 ? 'year' : 'years'})
                    </span>
                  )}
                </h3>

                {nanny.work_experiences?.length > 0 ? (
                  <div className="space-y-5">
                    {nanny.work_experiences.slice(0, 3).map((exp, i) => (
                      <div key={i} className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-6 border-l-4 border-primary">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="text-xl font-bold text-gray-900">{exp.position}</h4>
                            <p className="text-lg text-gray-700">{exp.institution_name}</p>
                            <p className="text-sm text-gray-500 mt-1">
                              {exp.start_date} – {exp.end_date || 'Present'}
                            </p>
                          </div>
                          {exp.is_current && (
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-bold">
                              Current
                            </span>
                          )}
                        </div>
                        {exp.responsibilities && (
                          <p className="mt-4 text-gray-700 italic leading-relaxed">
                            "{exp.responsibilities}"
                          </p>
                        )}
                      </div>
                    ))}
                    {nanny.work_experiences.length > 3 && (
                      <p className="text-center text-gray-600 mt-4 italic">
                        + {nanny.work_experiences.length - 3} more experience{nanny.work_experiences.length - 3 > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-lg italic">No experience listed yet</p>
                )}
              </div>

              {/* Qualifications */}
              {nanny.qualifications?.length > 0 && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                    <Award size={32} />
                    Qualifications & Certifications
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {nanny.qualifications.map((q, i) => (
                      <div key={i} className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 flex items-center gap-4">
                        <CheckCircle size={28} className="text-blue-600 flex-shrink-0" />
                        <div>
                          <p className="font-bold text-lg text-blue-900">{q.qualification_name}</p>
                          <p className="text-blue-700">{q.institution}</p>
                          {q.year_obtained && <p className="text-sm text-blue-600">• {q.year_obtained}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 justify-center mt-12 pt-8 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={onClose}
                className="px-10 py-5 text-xl font-semibold"
              >
                Close
              </Button>
              <Button
                onClick={() => setOfferOpen(true)}
                className="px-16 py-6 bg-gradient-to-r from-primary to-secondary text-white font-black text-2xl rounded-2xl shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all"
              >
                Make an Offer
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Offer Modal */}
      <MakeOfferModal
        nanny={nanny}
        isOpen={offerOpen}
        onClose={() => setOfferOpen(false)}
        onSuccess={() => {
          success('Offer sent successfully!');
          setOfferOpen(false);
          onClose();
        }}
      />
    </>
  );
}